/**
 * Global notification store for the shared API layer (issue #1963, Phase 1).
 *
 * Provides the single sink for user-facing messages that the shared error
 * handler ({@link handleApiError}) writes to, replacing the per-component
 * snackbar bookkeeping called out in epic #1961. `GlobalNotificationHost.vue`
 * renders this queue once at the app root; components/services only push.
 */

import { defineStore } from "pinia";
import { ref } from "vue";

export type NotificationType = "error" | "success" | "info" | "warning";

export interface AppNotification {
  id: number;
  type: NotificationType;
  message: string;
  /** Server request id, shown as a support reference on errors. */
  requestId?: string;
  /** Auto-dismiss delay in ms (passed straight to v-snackbar). */
  timeout: number;
}

export interface NotifyOptions {
  requestId?: string;
  timeout?: number;
}

const DEFAULT_TIMEOUTS: Record<NotificationType, number> = {
  error: 8000,
  warning: 6000,
  success: 4000,
  info: 4000,
};

let nextId = 0;

export const useNotificationStore = defineStore("notification", () => {
  const notifications = ref<AppNotification[]>([]);

  function notify(type: NotificationType, message: string, options: NotifyOptions = {}): number {
    const id = ++nextId;
    notifications.value.push({
      id,
      type,
      message,
      requestId: options.requestId,
      timeout: options.timeout ?? DEFAULT_TIMEOUTS[type],
    });
    return id;
  }

  function notifyError(message: string, options?: NotifyOptions): number {
    return notify("error", message, options);
  }

  function notifySuccess(message: string, options?: NotifyOptions): number {
    return notify("success", message, options);
  }

  function notifyInfo(message: string, options?: NotifyOptions): number {
    return notify("info", message, options);
  }

  function notifyWarning(message: string, options?: NotifyOptions): number {
    return notify("warning", message, options);
  }

  function dismiss(id: number): void {
    notifications.value = notifications.value.filter((n) => n.id !== id);
  }

  function clear(): void {
    notifications.value = [];
  }

  return {
    notifications,
    notify,
    notifyError,
    notifySuccess,
    notifyInfo,
    notifyWarning,
    dismiss,
    clear,
  };
});
